import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Form, Row, Col, Button, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SelectProvince from "components/custom/locations/SelectProvince";
import SelectCity from "components/custom/locations/SelectCity";
import SearchSelect from "components/common/SearchSelect";
import { fetchProvinces, fetchCities } from "api/menu/master-files/locations/actions";
import { fetchSupervisors, fetchHR } from "actions/employeeActions";
import { ApplicantStatus } from "data/applicant-statuses";

// Period options
const PERIOD_OPTIONS = {
    TODAY: 'today',
    LAST_3_DAYS: 'last3days',
    LAST_WEEK: 'lastweek',
    LAST_2_WEEKS: 'last2weeks',
    LAST_MONTH: 'lastmonth',
    CUSTOM: 'custom'
};

class ApplicantsFilter extends Component {
    constructor(props) {
        super(props);
        
        // Load saved filters from localStorage
        const savedFilters = this.loadFiltersFromStorage();
        
        this.state = {
            filters: savedFilters || {
                jobPostingId: null,
                provinceId: null,
                cityId: null,
                assignedToId: null,
                period: PERIOD_OPTIONS.LAST_2_WEEKS,
                startDate: '',
                endDate: '',
                includeInactivePostings: false
            }
        };
    }

    getStorageKey = () => {
        // Include the environment URL to separate filters per environment
        const envKey = window.location.origin;
        return `applicantsFilterCriteria_${envKey}`;
    };

    loadFiltersFromStorage = () => {
        try {
            const storageKey = this.getStorageKey();
            const savedFilters = localStorage.getItem(storageKey);
            if (savedFilters) {
                return JSON.parse(savedFilters);
            }
        } catch (error) {
            console.warn('Failed to load saved filter criteria:', error);
        }
        return null;
    };

    saveFiltersToStorage = (filters) => {
        try {
            const storageKey = this.getStorageKey();
            localStorage.setItem(storageKey, JSON.stringify(filters));
        } catch (error) {
            console.warn('Failed to save filter criteria:', error);
        }
    };

    calculateStartDateForPeriod = (period) => {
        const now = new Date();
        let startDate = new Date();
        
        switch (period) {
            case PERIOD_OPTIONS.TODAY:
                startDate.setHours(0, 0, 0, 0);
                break;
            case PERIOD_OPTIONS.LAST_3_DAYS:
                startDate.setDate(now.getDate() - 3);
                startDate.setHours(0, 0, 0, 0);
                break;
            case PERIOD_OPTIONS.LAST_WEEK:
                startDate.setDate(now.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                break;
            case PERIOD_OPTIONS.LAST_2_WEEKS:
                startDate.setDate(now.getDate() - 14);
                startDate.setHours(0, 0, 0, 0);
                break;
            case PERIOD_OPTIONS.LAST_MONTH:
                startDate.setDate(now.getDate());
                startDate.setMonth(now.getMonth() - 1);
                startDate.setHours(0, 0, 0, 0);
                break;
            default:
                return null;
        }
        
        return startDate.toISOString();
    };

    getPeriodLabel = (period) => {
        switch (period) {
            case PERIOD_OPTIONS.TODAY:
                return 'Today';
            case PERIOD_OPTIONS.LAST_3_DAYS:
                return 'Last 3 Days';
            case PERIOD_OPTIONS.LAST_WEEK:
                return 'Last Week';
            case PERIOD_OPTIONS.LAST_2_WEEKS:
                return 'Last 2 Weeks';
            case PERIOD_OPTIONS.LAST_MONTH:
                return 'Last Month';
            case PERIOD_OPTIONS.CUSTOM:
                return 'Custom';
            default:
                return '';
        }
    };

    componentDidMount() {
        this.props.fetchProvinces(1);
        this.props.fetchSupervisors();
        this.props.fetchHR();
        
        // Load cities for saved province if exists
        const { filters } = this.state;
        if (filters.provinceId) {
            this.props.fetchCities(filters.provinceId);
        }
        
        // Auto-apply saved filters if they exist
        if (filters.jobPostingId || filters.provinceId || filters.cityId || filters.assignedToId || 
            filters.period !== PERIOD_OPTIONS.LAST_2_WEEKS || 
            (filters.period === PERIOD_OPTIONS.CUSTOM && (filters.startDate || filters.endDate)) ||
            filters.includeInactivePostings) {
            // Apply the saved filters automatically
            setTimeout(() => this.handleSearch(), 200);
        }
    }

    handleFilterChange = (field, value) => {
        let newFilters = { ...this.state.filters };
        
        // Handle mutual exclusivity between jobPostingId and province/city
        if (field === 'jobPostingId') {
            newFilters = {
                ...newFilters,
                jobPostingId: value,
                provinceId: null,
                cityId: null
            };
        } else if (field === 'provinceId') {
            newFilters = {
                ...newFilters,
                provinceId: value,
                cityId: null,
                jobPostingId: null
            };
        } else if (field === 'cityId') {
            newFilters = {
                ...newFilters,
                cityId: value,
                jobPostingId: null
            };
        } else {
            newFilters[field] = value;
        }
        
        this.setState({ filters: newFilters });
        this.saveFiltersToStorage(newFilters);
    };

    handleSearch = () => {
        const { onSearch } = this.props;
        const { filters } = this.state;
        
        // Prepare API filters based on period selection
        let apiFilters = {
            jobPostingId: filters.jobPostingId,
            provinceId: filters.provinceId,
            cityId: filters.cityId,
            assignedToId: filters.assignedToId,
            includeInactivePostings: filters.includeInactivePostings
        };

        // Handle date filtering based on period
        if (filters.period === PERIOD_OPTIONS.CUSTOM) {
            // For Custom: Send dates as user provided
            if (filters.startDate) {
                apiFilters.startDate = new Date(filters.startDate).toISOString();
            }
            if (filters.endDate) {
                apiFilters.endDate = new Date(filters.endDate).toISOString();
            }
        } else {
            // For predefined periods: Only send calculated startDate
            apiFilters.startDate = this.calculateStartDateForPeriod(filters.period);
            // Do not send endDate - open ended range up to NOW
        }

        onSearch(apiFilters);
    };

    handleReset = () => {
        const defaultFilters = {
            jobPostingId: null,
            provinceId: null,
            cityId: null,
            assignedToId: null,
            period: PERIOD_OPTIONS.LAST_2_WEEKS,
            startDate: '',
            endDate: '',
            includeInactivePostings: false
        };
        
        this.setState({ filters: defaultFilters });
        this.saveFiltersToStorage(defaultFilters);
        
        // Clear saved filters from localStorage
        try {
            const storageKey = this.getStorageKey();
            localStorage.removeItem(storageKey);
        } catch (error) {
            console.warn('Failed to clear saved filter criteria:', error);
        }
    };

    handleCloseSearch = () => {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
    };

    render() {
        const { filters } = this.state;
        const { supervisors, hr, loading, isSupervisor, jobPostings } = this.props;

        // Create assigned to options
        const hrOptions = (hr || []).map(item => ({ value: item.id, label: item.name }));
        const supervisorOptions = (supervisors || []).map(item => ({ value: item.id, label: item.name }));
        
        const assignedToOptions = [
            { label: "HR Representatives", options: hrOptions },
            { label: "Supervisors", options: supervisorOptions }
        ];

        const currentAssignedTo = [...hrOptions, ...supervisorOptions]
            .find(o => o.value === filters.assignedToId) || null;

        // Create job postings options
        const jobPostingOptions = (jobPostings || []).map(jp => ({
            value: jp.id,
            label: `${jp.title} - ${jp.cityName}, ${jp.provinceName}${!jp.isActive ? ' (Inactive)' : ''}`
        }));

        const currentJobPosting = jobPostingOptions.find(o => o.value === filters.jobPostingId) || null;

        return (
            <Card className="mb-3">
                    <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0">Applicants Filter</h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            {/* Column 1: Job Posting, Province, City */}
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Job Posting</Form.Label>
                                    <SearchSelect
                                        className="custom-react-select"
                                        options={jobPostingOptions}
                                        value={currentJobPosting}
                                        onChange={(value) => this.handleFilterChange('jobPostingId', value?.value || null)}
                                        isClearable={true}
                                        isSearchable={true}
                                        placeholder="Select a job posting..."
                                        isDisabled={filters.provinceId || filters.cityId}
                                    />
                                </Form.Group>
                                <div className="text-center mb-2">
                                    <span className="text-muted font-weight-bold">OR</span>
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Province</Form.Label>
                                            <SelectProvince
                                                provinceId={filters.provinceId}
                                                onChange={(value) => this.handleFilterChange('provinceId', value?.value || null)}
                                                isClearable={true}
                                                isDisabled={filters.jobPostingId}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>City</Form.Label>
                                            <SelectCity
                                                provinceId={filters.provinceId}
                                                value={filters.cityId}
                                                onChange={(value) => this.handleFilterChange('cityId', value?.value || null)}
                                                isClearable={true}
                                                isDisabled={filters.jobPostingId || !filters.provinceId}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Column 2: Period, Custom Dates */}
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Period</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={filters.period}
                                        onChange={(e) => this.handleFilterChange('period', e.target.value)}
                                    >
                                        <option value={PERIOD_OPTIONS.TODAY}>Today</option>
                                        <option value={PERIOD_OPTIONS.LAST_3_DAYS}>Last 3 Days</option>
                                        <option value={PERIOD_OPTIONS.LAST_WEEK}>Last Week</option>
                                        <option value={PERIOD_OPTIONS.LAST_2_WEEKS}>Last 2 Weeks</option>
                                        <option value={PERIOD_OPTIONS.LAST_MONTH}>Last Month</option>
                                        <option value={PERIOD_OPTIONS.CUSTOM}>Custom</option>
                                    </Form.Control>
                                </Form.Group>
                                {filters.period === PERIOD_OPTIONS.CUSTOM && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Start Date (Optional)</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={filters.startDate}
                                                    onChange={(e) => this.handleFilterChange('startDate', e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>End Date (Optional)</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={filters.endDate}
                                                    onChange={(e) => this.handleFilterChange('endDate', e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </Col>

                            {/* Column 3: Assigned To, Include Inactive */}
                            <Col md={4}>
                                {!isSupervisor && (
                                    <Form.Group>
                                        <Form.Label>Assigned To</Form.Label>
                                        <SearchSelect
                                            className="custom-react-select"
                                            options={assignedToOptions}
                                            value={currentAssignedTo}
                                            onChange={(value) => this.handleFilterChange('assignedToId', value?.value || null)}
                                            isClearable={true}
                                            isSearchable={true}
                                            isLoading={this.props.employeeLoading}
                                        />
                                    </Form.Group>
                                )}
                                <Form.Group className={!isSupervisor ? 'mt-3' : ''}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={filters.includeInactivePostings}
                                        onChange={(e) => this.handleFilterChange('includeInactivePostings', e.target.checked)}
                                        label="Include inactive postings"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12} className="text-right">
                                <Button
                                    variant="secondary"
                                    className="mr-2"
                                    onClick={this.handleCloseSearch}
                                >
                                    <FontAwesomeIcon icon="times" className="mr-1" />
                                    Close Search
                                </Button>
                                <Button
                                    variant="primary"
                                    className="mr-2"
                                    onClick={this.handleSearch}
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon="search" className="mr-1" />
                                    Search
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={this.handleReset}
                                >
                                    <FontAwesomeIcon icon="redo" className="mr-1" />
                                    Reset
                                </Button>
                            </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }
}

ApplicantsFilter.propTypes = {
    onSearch: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    applicants: PropTypes.array.isRequired,
    jobPostings: PropTypes.array.isRequired,
    supervisors: PropTypes.array.isRequired,
    hr: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    employeeLoading: PropTypes.bool.isRequired,
    isSupervisor: PropTypes.bool.isRequired,
    fetchProvinces: PropTypes.func.isRequired,
    fetchCities: PropTypes.func.isRequired,
    fetchSupervisors: PropTypes.func.isRequired,
    fetchHR: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    applicants: state.applicants.applicants,
    jobPostings: state.jobPostings.jobPostings,
    supervisors: state.employee.supervisors,
    hr: state.employee.hr,
    loading: state.applicants.loading,
    employeeLoading: state.employee.loading
});

const mapDispatchToProps = dispatch => ({
    fetchProvinces: bindActionCreators(fetchProvinces, dispatch),
    fetchCities: bindActionCreators(fetchCities, dispatch),
    fetchSupervisors: bindActionCreators(fetchSupervisors, dispatch),
    fetchHR: bindActionCreators(fetchHR, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicantsFilter);
